import useSWR from 'swr'
import { useToast } from '@/hooks/use-toast'
import { apiFetch } from '@/utils'

export interface TokenInfo {
	price: number
	change24h: number | null
	volume24h: number | null
	marketCap: number | null
	liquidity: number | null
}

interface PriceData {
	sech: TokenInfo
	eth: TokenInfo
	usdc: TokenInfo
}

type CurrencyType = 'sech' | 'eth' | 'usdc'

// Fallback values for when API calls fail
const FALLBACK_PRICES: any = {
	// sech: { price: 0 },
	// eth: { price: 0 },
	// usdc: { price: 0 }
}

// Cache configuration
const CACHE_DURATION = 30_000 //30 sec
const MAX_RETRIES = 3
const RETRY_DELAY = 2000

let priceCache = {
	data: null as PriceData | null,
	timestamp: 0,
	error: null as Error | null
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

async function fetchPricesWithRetry(retries = MAX_RETRIES): Promise<PriceData> {
	try {
		const response = await apiFetch(`/v1/prices?t=${Date.now()}`)

		if(!response.ok) {
			const errorData = await response.json()
			throw new Error(
				errorData.message || `HTTP error! status: ${response.status}`
			)
		}

		const pricesResult = await response.json()

		return {
			eth: pricesResult.data.eth,
			usdc: pricesResult.data.usdc,
			sech: pricesResult.data.sech
		}
	} catch(error) {
		if(retries > 0) {
			await sleep(RETRY_DELAY)
			return fetchPricesWithRetry(retries - 1)
		}
		throw error
	}
}

async function fetchCoinMarketCapPrices(): Promise<PriceData> {
	const now = Date.now()

	// Return cached data if valid
	if(priceCache.data && now - priceCache.timestamp < CACHE_DURATION) {
		if(priceCache.error) {
			throw priceCache.error
		}
		return priceCache.data
	}

	try {
		const data = await fetchPricesWithRetry()

		// Update cache
		priceCache = {
			data,
			timestamp: now,
			error: null
		}

		return data
	} catch(error) {
		// Update cache with error
		priceCache = {
			data: FALLBACK_PRICES,
			timestamp: now,
			error:
				error instanceof Error ? error : new Error('Failed to fetch prices')
		}

		console.error('Price fetching error:', error)
		throw error
	}
}

export function usePrices() {
	const { toast } = useToast()

	const { data, error, isValidating } = useSWR<PriceData>(
		'prices',
		fetchCoinMarketCapPrices,
		{
			refreshInterval: 30_000, // Refresh every 30 sec
			dedupingInterval: 30_000,
			revalidateOnMount: true,
			fallbackData: FALLBACK_PRICES,
			errorRetryCount: 3,
			onError: (err) => {
				const errorMessage =
					err instanceof Error ? err.message : 'Failed to fetch prices'
				toast({
					title: 'Price Update Notice',
					description: `Using fallback prices: ${errorMessage}`,
					variant: 'destructive'
				})
			}
		}
	)

	const currentPrices = data || FALLBACK_PRICES

	// Convert USD amount to token amount
	const convertUsdToToken = (usdAmount: number, currency: CurrencyType): number => {
		const pricePerToken = currentPrices[currency]?.price
		if(!pricePerToken || pricePerToken <= 0) {
			console.warn(`Invalid price for ${currency}: ${pricePerToken}`)
			return 0
		}
		return usdAmount / pricePerToken
	}

	// Convert token amount to USD
	const convertTokenToUsd = (tokenAmount: number, currency: CurrencyType): number => {
		const safeAmount = typeof tokenAmount === 'number' && !isNaN(tokenAmount) ? tokenAmount : 0
		const pricePerToken = currentPrices[currency]?.price ?? 0
		return safeAmount * pricePerToken
	}

	// Get the best token amount for a USD value (highest token quantity)
	const getBestTokenForUsd = (usdAmount: number): { currency: CurrencyType; amount: number } => {
		const conversions = {
			sech: convertUsdToToken(usdAmount, 'sech'),
			eth: convertUsdToToken(usdAmount, 'eth'),
			usdc: convertUsdToToken(usdAmount, 'usdc')
		}

		const best = Object.entries(conversions).reduce((max, [currency, amount]) =>
				amount > max.amount ? { currency: currency as CurrencyType, amount } : max
			, { currency: 'sech' as CurrencyType, amount: conversions.sech })

		return best
	}

	return {
		prices: currentPrices,
		isLoading: !error && !data,
		isRefreshing: isValidating,
		error,
		lastUpdated: priceCache.timestamp,
		convertUsdToToken,
		convertTokenToUsd,
		getBestTokenForUsd
	}
}
